import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import AlertModal from '../../components/AlertModal';


const PaymentVoucher = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openForm, setOpenForm] = useState(false);

  // Form state for adding payment
  const [formData, setFormData] = useState({
    ledger: '',
    amount: '',
    remark: ''
  });

  // Modal state
  const [alertConfig, setAlertConfig] = useState({ open: false, title: 'Alert', message: '', type: 'info' });

  const showAlert = (message, type = 'info', title = 'Alert') => {
    setAlertConfig({ open: true, title, message, type });
  };


  // Sample data for Payment Voucher List
  const [paymentVoucherData, setPaymentVoucherData] = useState([
    {
      id: 1,
      ledger: 'Office Rent',
      remark: 'Monthly office rent payment for October 2024',
      amount: 25000,
    },
    {
      id: 2,
      ledger: 'Electricity Bill',
      remark: 'Electricity bill payment for September 2024',
      amount: 5500,
    },
    {
      id: 3,
      ledger: 'Staff Salary',
      remark: 'Salary payment for all staff members',
      amount: 150000,
    },
    {
      id: 4,
      ledger: 'Office Supplies',
      remark: 'Purchase of stationery and office equipment',
      amount: 8750,
    },
    {
      id: 5,
      ledger: 'Marketing Expenses',
      remark: 'Advertisement and promotional activities',
      amount: 12000,
    },
  ]);

  // Ledger options for dropdown
  const ledgerOptions = [
    'Office Rent',
    'Electricity Bill',
    'Staff Salary',
    'Office Supplies',
    'Marketing Expenses',
    'Internet & Telephone',
    'Travel Expenses',
    'Maintenance',
    'Insurance Premium',
    'Legal Fees',
    'Bank Charges',
    'Professional Fees',
    'Fuel Expenses',
    'Printing & Stationery'
  ];

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleAddPayment = () => {
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setFormData({
      ledger: '',
      amount: '',
      remark: ''
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAdd = () => {
    if (formData.ledger && formData.amount && formData.remark) {
      const newPayment = {
        id: paymentVoucherData.length + 1,
        ledger: formData.ledger,
        amount: parseFloat(formData.amount),
        remark: formData.remark
      };

      setPaymentVoucherData(prev => [...prev, newPayment]);
      showAlert('Payment voucher added successfully!', 'success', 'Success');
      handleCloseForm();
    } else {
      showAlert('Please fill all fields', 'warning', 'Missing Information');
    }
  };

  const handleClear = () => {
    setFormData({
      ledger: '',
      amount: '',
      remark: ''
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="h4" component="h1" gutterBottom>
            Payment Voucher List
          </Typography>
        </Grid>

        {/* Add Payment Button - Right Side */}
        <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddPayment}
            sx={{
              px: 3,
              py: 1,
              fontSize: '0.9rem',
              fontWeight: 600,
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
              '&:hover': {
                boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            Add Payment
          </Button>
        </Grid>
      </Grid>

      {/* Add Payment Form Dialog */}
      <Dialog
        open={openForm}
        onClose={handleCloseForm}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Add Payment Voucher
          <Button
            onClick={handleCloseForm}
            size="small"
            sx={{ minWidth: 'auto', p: 1 }}
          >
            <CloseIcon />
          </Button>
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Select Ledger</InputLabel>
                <Select
                  value={formData.ledger}
                  label="Select Ledger"
                  onChange={(e) => handleInputChange('ledger', e.target.value)}
                >
                  <MenuItem value="">
                    <em>Select Ledger</em>
                  </MenuItem>
                  {ledgerOptions.map((ledger) => (
                    <MenuItem key={ledger} value={ledger}>
                      {ledger}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={formData.amount}
                onChange={(e) => handleInputChange('amount', e.target.value)}
                placeholder="Enter amount"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Remark"
                multiline
                rows={3}
                value={formData.remark}
                onChange={(e) => handleInputChange('remark', e.target.value)}
                placeholder="Enter remark/description"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ClearIcon />}
            onClick={handleClear}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAdd}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Voucher List Table */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Voucher List
        </Typography>

        <Typography variant="body2" color="primary" sx={{ mb: 3, fontWeight: 'bold' }}>
          Total Records: {paymentVoucherData.length}
        </Typography>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Ledger</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Remark</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paymentVoucherData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                  <TableRow
                    key={row.id}
                    sx={{
                      '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                      '&:hover': { backgroundColor: '#e3f2fd' }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                        {row.ledger}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', maxWidth: '300px' }}>
                        {row.remark}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'success.main' }}>
                        {formatCurrency(row.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<SaveIcon />}
                        sx={{ fontSize: '0.75rem' }}
                      >
                        Save
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={paymentVoucherData.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          
          sx={{ mt: 2 }}
        />
      </Paper>

      <AlertModal
        open={alertConfig.open}
        title={alertConfig.title}
        message={alertConfig.message}
        type={alertConfig.type}
        onClose={() => setAlertConfig({ ...alertConfig, open: false })}
      />
    </Box>
  );
};

export default PaymentVoucher;